import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useRef, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import Video from '@/components/atoms/video/video';
import styles from './secondary-header-item.module.scss';

export type SecondaryHeaderItemProps = Content.Properties.MediaItem & {
  /** The class name of the item */
  className?: string;
};

const SecondaryHeaderItem = ({
  className,
  ...rest
}: SecondaryHeaderItemProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const isVideo = 'video' in rest;
  const { t } = useI18n();

  const handleClickPlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }

      setIsPlaying(!isPlaying);
    }
  };

  const renderMedia = () => {
    return isVideo ? (
      <Video
        ref={videoRef}
        autoPlay
        isPlaying={isPlaying}
        placeholder={rest.video.placeholderImage?.url}
        src={rest.video.url}
      />
    ) : (
      <Image
        alt={rest.image.altText}
        className={styles.image}
        focalPoint={rest.image.focalPoint}
        height={185}
        priority
        src={rest.image.url}
        srcSet={{
          desktop: {
            height: 756,
            width: 1344,
          },
          laptop: {
            height: 532,
            width: 945,
          },
          tablet: {
            height: 397,
            width: 705,
          },
        }}
        unoptimized
        width={328}
      />
    );
  };

  return (
    <div className={classNames(styles.component, className)}>
      {renderMedia()}

      {isVideo && (
        <button
          aria-label={
            isPlaying
              ? t('items.common.words.pauseVideo')
              : t('items.common.words.playVideo')
          }
          className={styles.videoIcon}
          onClick={handleClickPlayPause}
        >
          {isPlaying ? (
            <Icon name="Pause" size={16} />
          ) : (
            <Icon name="Play" size={16} />
          )}
        </button>
      )}
    </div>
  );
};

SecondaryHeaderItem.displayName = 'SecondaryHeaderItem';

export default SecondaryHeaderItem;
